/*
 * Campaign gift ladder
 * --------------------
 * Two thresholds, two gifts, email traffic only.
 *
 *   Tier 1 — $25 of qualifying spend  →  gift 1, added the moment they arrive
 *   Tier 2 — $55 of qualifying spend  →  gift 2, added when they cross it
 *
 * Three things worth knowing before changing any of this:
 *
 * 1. The Shopify discounts never ADD anything. They only price a gift at $0 once
 *    it is already in the cart and the spend condition is met. Everything below is
 *    what actually puts the items there — which is why the offer can be an open
 *    automatic discount and still reach only the people we invite.
 *
 * 2. "Invited" means arrived with ?gift=1. We persist that as a cart attribute so
 *    the Liquid progress bar can gate on it too, and so it survives them wandering
 *    off the landing page. Nobody else sees or gets anything.
 *
 * 3. Qualifying spend excludes the gifts themselves, mirroring the collection rules
 *    in Shopify. If the two ever disagree, we add a gift the discount won't zero,
 *    the fail-safe rips it back out, and the shopper is locked out for the session.
 *
 * Config comes from snippets/new-moon-gift.liquid, which stops rendering outside the
 * campaign window — so a forwarded link can never add a line they'd be charged for.
 */
(function () {
  const config = window.WVCampaignGift;
  if (!config || !config.tiers || !config.tiers.length) return;

  const TIERS = config.tiers.map((tier) => ({
    // Carry the whole tier through. A hand-listed projection silently drops any campaign
    // key the snippet adds, which cost a debugging round on 19 Aug. The normalised
    // fields below deliberately override the spread.
    ...tier,
    threshold: Number(tier.threshold),
    variantId: Number(tier.variantId),
    label: tier.label,
    // Each tier states its own threshold on the cart line. A single shared sentence
    // would have to say something vague like "reaches the tier", which means nothing
    // to a shopper reading their basket.
    lineValue: tier.lineValue,
    // Tier 1 goes in the bag the moment they arrive and stays there, priced until they
    // qualify. That endowment — "it's already mine, I just have to reach $25" — is what
    // the last campaign converted on, so it is deliberate that the line shows $39 for a
    // while. Later tiers only appear once earned, or the cart is a wall of unexplained
    // full-price items before the shopper has done anything.
    addOnArrival: !!tier.addOnArrival,
  }));

  const GIFT_IDS = TIERS.map((tier) => tier.variantId);
  const ATTR = 'wv-campaign';
  const INVITED_KEY = 'wv:campaign-invited';
  const DISMISS_KEY = 'wv:campaign-dismissed:';

  function param(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function readFlag(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeFlag(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode — the cart attribute still carries the invite */
    }
  }

  const arrivedFromEmail = param(config.param || 'gift') === '1';

  function getCart() {
    return fetch(window.routes.cart_url + '.js').then((response) => response.json());
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Mirrors the Shopify collection rules: the gifts never count toward any threshold,
  // so a free necklace can't push someone over the line for the free bracelet.
  function qualifyingSpend(cart) {
    return cart.items.reduce(
      (total, item) =>
        GIFT_IDS.indexOf(item.variant_id) === -1 ? total + item.original_line_price : total,
      0
    );
  }

  function lineFor(cart, variantId) {
    return cart.items.find((item) => item.variant_id === variantId) || null;
  }

  /* ------------------------------------------------------------------ cart writes */

  function sectionsToRequest() {
    const sections = [];
    if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');

    const drawer = document.querySelector('cart-drawer');
    const wrapper = drawer && drawer.closest('.shopify-section');
    if (wrapper && wrapper.id) sections.push(wrapper.id.replace(/^shopify-section-/, ''));

    return sections;
  }

  function withSections(body) {
    const sections = sectionsToRequest();
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => (response.ok ? response.json() : null));
  }

  function addGift(tier) {
    return post(
      window.routes.cart_add_url + '.js',
      withSections({
        items: [
          {
            id: tier.variantId,
            quantity: 1,
            properties: { [config.lineProperty]: tier.lineValue || config.lineValue },
          },
        ],
      })
    );
  }

  function removeLine(key) {
    return post(window.routes.cart_change_url + '.js', withSections({ id: key, quantity: 0 }));
  }

  function markInvited() {
    writeFlag(INVITED_KEY, config.key);
    return post(window.routes.cart_update_url + '.js', {
      attributes: { [ATTR]: config.key },
    });
  }

  function applySections(result) {
    if (!result || !result.sections) return;

    Object.keys(result.sections).forEach((id) => {
      const target =
        id === 'cart-icon-bubble' ? document.getElementById('cart-icon-bubble') : document.querySelector('#CartDrawer');
      if (!target) return;

      const selector = id === 'cart-icon-bubble' ? '.shopify-section' : '#CartDrawer';
      const parsed = new DOMParser().parseFromString(result.sections[id], 'text/html');
      const source = parsed.querySelector(selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  // Dawn stamps is-empty on <cart-drawer> when the page renders with an empty cart and
  // only its own component clears it. We write to the cart directly, so without this the
  // drawer hides correctly-rendered line items until the next full page load.
  function syncDrawer(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (cart.item_count > 0) drawer.classList.remove('is-empty');
    else drawer.classList.add('is-empty');
  }

  /* ------------------------------------------------------------------ reconcile */

  // Presence at the end of the previous pass, per tier. A gift that was here and is now
  // gone — without us having removed it — was taken out by the shopper.
  const lastPresent = {};

  async function reconcile(cart) {
    let changed = null;

    for (const tier of TIERS) {
      const spend = qualifyingSpend(cart);
      const line = lineFor(cart, tier.variantId);
      const eligible = spend >= tier.threshold;
      const key = DISMISS_KEY + tier.variantId;

      // Respect a shopper who took a gift back out. Without this, tier 1 re-adds itself
      // the instant they delete it — the cart refuses to let go of the item and the whole
      // thing feels broken. Detecting it needs the previous pass, because by the time we
      // look the line is simply absent, which is indistinguishable from never-added.
      if (!line && lastPresent[tier.variantId]) writeFlag(key, '1');

      const dismissed = readFlag(key) === '1';
      const wanted = (tier.addOnArrival || eligible) && !dismissed;

      if (!line && dismissed) {
        lastPresent[tier.variantId] = false;
        continue;
      }

      if (wanted && !line) {
        const result = await addGift(tier);
        if (!result) {
          lastPresent[tier.variantId] = false;
          continue;
        }

        cart = await getCart();
        const added = lineFor(cart, tier.variantId);
        lastPresent[tier.variantId] = !!added;

        // Earned tiers must come back at zero. If one doesn't — discount not started,
        // expired, or scoped differently — pull it straight out rather than leave a
        // charged line in a cart the shopper never chose. Tier 1 is exempt: it is
        // supposed to sit at full price until they reach the threshold.
        if (!tier.addOnArrival && added && added.final_line_price > 0) {
          writeFlag(key, '1');
          const undo = await removeLine(added.key);
          cart = await getCart();
          lastPresent[tier.variantId] = false;
          changed = undo || changed;
          continue;
        }

        changed = result;
      } else if (line && !eligible && !tier.addOnArrival) {
        // Dropped back under the threshold — take the earned gift out again, or they are
        // charged for something the discount no longer covers.
        const result = await removeLine(line.key);
        if (result) {
          cart = await getCart();
          changed = result;
        }
        lastPresent[tier.variantId] = false;
      } else {
        lastPresent[tier.variantId] = !!line;
      }
    }

    return { cart: cart, changed: changed };
  }

  /* ------------------------------------------------------------------ notice */

  function host() {
    let slot = document.querySelector('.wv-new-moon-note');
    if (slot) return slot;

    slot = document.createElement('div');
    slot.className = 'wv-new-moon-note';

    // On mobile the info column sits below the gallery, which buries the notice under
    // the sticky Add to bag bar. Put it above the images instead.
    if (window.matchMedia('(max-width: 749px)').matches) {
      const media = document.querySelector('.product__media-wrapper');
      if (media && media.parentElement) {
        media.parentElement.insertBefore(slot, media);
        return slot;
      }
    }

    const container = document.querySelector('.product__info-container');
    if (!container) return null;

    const price = container.querySelector('.price');
    let block = price;
    while (block && block.parentElement !== container) block = block.parentElement;

    if (block) block.insertAdjacentElement('afterend', slot);
    else container.prepend(slot);

    return slot;
  }

  function render(cart) {
    if (!config.onGiftProduct) return;

    const slot = host();
    if (!slot) return;

    const spend = qualifyingSpend(cart);
    const first = TIERS[0];
    const second = TIERS[1];
    const firstInCart = !!lineFor(cart, first.variantId);

    let body;

    // They took it out. Saying "already in your bag" here would be a plain lie, and the
    // offer still stands — so tell the truth and give them the way back.
    // A single-tier campaign leaves TIERS[1] undefined. Reading second.threshold here
    // threw a TypeError that killed the whole notice, so every use of `second` is now
    // behind a guard. Do not reintroduce a bare second.* in these two branches.
    const secondLine = second
      ? '<p class="wv-nm__sub">Reach <u>' + money(second.threshold) + '</u> and the <b>' +
        second.label + '</b> is free too.</p>'
      : '';
    const noCode = '<p class="wv-nm__sub"><b>There is no code to type.</b></p>';

    if (!firstInCart) {
      body =
        '<p class="wv-nm__line">The <b>' + first.label + '</b> is <b>FREE</b> when you spend ' +
        '<u>' + money(first.threshold) + '</u> or more on anything else.</p>' +
        (second ? secondLine : '') +
        '<button type="button" class="wv-nm__btn" data-campaign-gift-add>Put it back in my bag</button>';
    } else if (spend < first.threshold) {
      body =
        '<p class="wv-nm__line">Your <b>' + first.label + '</b> is already in your bag. ' +
        'Spend <u>' + money(first.threshold - spend) + ' more</u> on anything else and it ' +
        'becomes <b>FREE</b>.</p>' +
        (second ? secondLine : noCode);
    } else if (second && spend < second.threshold) {
      body =
        '<p class="wv-nm__line wv-nm__line--won">The ' + first.label + ' is <b>FREE</b>.</p>' +
        '<p class="wv-nm__sub">Add <u>' + money(second.threshold - spend) + ' more</u> and the <b>' +
        second.label + '</b> is free too.</p>';
    } else {
      body =
        '<p class="wv-nm__line wv-nm__line--won">' +
        (second ? 'Both gifts are' : 'Your <b>' + first.label + '</b> is') +
        ' <b>FREE</b>.</p>' +
        '<p class="wv-nm__sub">Already applied to your bag. <b>There is no code to type.</b></p>';
    }

    slot.innerHTML = '<p class="wv-nm__title">' + config.noticeTitle + '</p>' + body;
  }

  /* ------------------------------------------------------------------ run */

  let running = false;
  let pending = false;

  async function run() {
    // Our own cart writes re-enter here through the fetch hook. Dropping those wakeups
    // leaves the notice describing the cart as it was one step ago — so queue the latest
    // one instead of discarding it.
    if (running) {
      pending = true;
      return;
    }
    running = true;

    try {
      let cart = await getCart();

      const invited =
        arrivedFromEmail ||
        readFlag(INVITED_KEY) === config.key ||
        (cart.attributes && cart.attributes[ATTR] === config.key);

      // Everyone else shops normally and is told nothing. The discounts are open, but
      // without the gifts in their cart there is nothing for them to apply to.
      if (!invited) return;

      if (arrivedFromEmail && !(cart.attributes && cart.attributes[ATTR] === config.key)) {
        await markInvited();
      }

      const result = await reconcile(cart);
      cart = result.cart;

      if (result.changed) applySections(result.changed);
      syncDrawer(cart);
      render(cart);
    } catch (error) {
      /* never let a campaign break the storefront */
    } finally {
      running = false;
      if (pending) {
        pending = false;
        setTimeout(run, 60);
      }
    }
  }

  // "Put it back in my bag" — asking for it again is a change of mind, so clear the flag.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-campaign-gift-add]');
    if (!button) return;

    event.preventDefault();
    button.setAttribute('disabled', 'disabled');

    try {
      writeFlag(DISMISS_KEY + TIERS[0].variantId, '0');
      const result = await addGift(TIERS[0]);
      if (result) {
        lastPresent[TIERS[0].variantId] = true;
        applySections(result);
      }
      await run();
    } catch (error) {
      button.removeAttribute('disabled');
    }
  });

  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const originalFetch = window.fetch;

  window.fetch = function (resource) {
    const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
    const result = originalFetch.apply(this, arguments);

    if (CART_WRITE.test(url)) {
      result.then((response) => {
        if (response && response.ok) setTimeout(run, 300);
      }).catch(() => {});
    }

    return result;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
